import { useEffect, useState } from 'react'
import { Settings as SettingsIcon, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Loader, EmptyState } from '@/components/common/Loader'
import { settingsApi } from '@/api/settingsApi'
import { SETTING_META } from '@/lib/constants'
import { formatDate } from '@/lib/utils'

export default function Settings() {
  const [settings, setSettings] = useState([])
  const [loading, setLoading] = useState(true)
  const [values, setValues] = useState({})
  const [savingKey, setSavingKey] = useState(null)

  const load = async () => {
    setLoading(true)
    try {
      const rows = await settingsApi.list()
      setSettings(rows)
      const v = {}
      rows.forEach((row) => {
        v[row.key] = row.value
      })
      setValues(v)
    } catch {
      toast.error('Could not load settings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleSave = async (key) => {
    const value = values[key]
    if (value === undefined || String(value).trim() === '') {
      toast.error('Value cannot be empty')
      return
    }
    setSavingKey(key)
    try {
      const updated = await settingsApi.update(key, value)
      setSettings((arr) => arr.map((s) => (s.key === key ? updated : s)))
      toast.success('Setting saved')
    } catch {
      // toast handled
    } finally {
      setSavingKey(null)
    }
  }

  return (
    <div className="container-page py-6 sm:py-8">
      <PageHeader
        eyebrow="Clinic configuration"
        title="Settings"
        description="Tune the thresholds that power the drug-list filters. Changes apply instantly."
      />

      <div className="mx-auto mt-6 max-w-3xl">
        {loading ? (
          <Loader label="Loading settings…" />
        ) : settings.length === 0 ? (
          <div className="rounded-2xl bg-white shadow-soft ring-1 ring-ink-100">
            <EmptyState
              icon={<SettingsIcon className="h-6 w-6" />}
              title="No settings yet"
              description="The backend hasn't seeded any app settings."
            />
          </div>
        ) : (
          <div className="space-y-3">
            {settings.map((s) => {
              const meta = SETTING_META[s.key] || {}
              const label = meta.label || s.key
              const description = meta.description || s.description || ''
              const suffix = meta.suffix
              const inputType = meta.inputType || 'text'
              return (
                <div
                  key={s.key}
                  className="rounded-2xl bg-white p-5 shadow-soft ring-1 ring-ink-100"
                >
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                    <div className="max-w-xl">
                      <p className="font-semibold text-ink-900">{label}</p>
                      <p className="text-[11px] font-mono text-ink-400">{s.key}</p>
                      {description && (
                        <p className="mt-1 text-sm text-ink-500">{description}</p>
                      )}
                    </div>
                    <p className="text-[11px] text-ink-400">
                      Updated {formatDate(s.updated_at, { withTime: true })}
                    </p>
                  </div>
                  <div className="mt-3 flex items-end gap-2">
                    <div className="flex-1">
                      <div className="relative">
                        <Input
                          type={inputType}
                          min={meta.min}
                          value={values[s.key] ?? ''}
                          onChange={(e) =>
                            setValues((v) => ({ ...v, [s.key]: e.target.value }))
                          }
                          className={suffix ? 'pr-12' : ''}
                        />
                        {suffix && (
                          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-ink-500">
                            {suffix}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleSave(s.key)}
                      loading={savingKey === s.key}
                      disabled={values[s.key] === s.value}
                      leftIcon={<Save className="h-4 w-4" />}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
